// Fees → Policies — the resolution chain at a glance.
//
// Three levels, most general to most specific:
//
//   Platform default    the rate every market falls back to
//   Category overrides  one rate for a whole category
//   Market overrides    one rate for a single market
//
// plus Scheduled changes (saved ahead of time, not yet active). Every
// row leads with the entity's name and opens the full-page policy
// detail; creation happens on a dedicated page with the same room.
//
// Currency is the second axis, and since SM-510 it is a real one: a
// policy prices exactly ONE currency, so `?currency=` narrows the QUERY
// and the server returns that currency's platform default, category
// overrides and market overrides. Absent means every currency, and each
// row names its own.
//
// A level can therefore carry up to one policy per currency, plus the
// pre-SM-510 legacy row that still prices every book at that level until
// an operator replaces it. See components/fees/feeCurrency.ts.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  cancelScheduledFeeProfile,
  fetchCategories,
  fetchFeeProfiles,
  fetchRules,
  fetchScheduledFeeProfiles,
  feeKeys,
  keys,
  type FeeProfile,
} from "@/lib/queries";
import { Input } from "@/components/ui/Input";
import { Skeleton } from "@/components/ui/Skeleton";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import {
  PolicyEmpty,
  PolicyRow,
  PolicySection,
} from "@/components/fees/PolicyList";
import { LoadFailed } from "@/components/fees/LoadFailed";
import {
  redemptionRateOf,
  scopeNoun,
  setsRedemptionRate,
} from "@/components/fees/policy";
import { useMarketNames } from "@/hooks/useCatalogNames";
import { fmtRelative } from "@/lib/format";
import { FeeCurrencyFilter } from "@/components/fees/FeeCurrencyFilter";
import {
  feeCurrencySearch,
  LEGACY_FEE_CURRENCY,
  parseFeeCurrencySearch,
  scopedCurrency,
  type FeeCurrencyParam,
} from "@/components/fees/feeCurrency";
import { useCurrencyName } from "@/components/currency/CurrencyPresentationProvider";

export const Route = createFileRoute("/_app/treasury/fees/policies/")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { currency?: FeeCurrencyParam } => {
    const currency = parseFeeCurrencySearch(search.currency);
    return currency ? { currency } : {};
  },
  component: PoliciesPage,
});

// Page-level fetch window. Market overrides beyond the first page of
// the catalog resolve their names via useMarketNames' per-id fallback.
const PAGE_FETCH_LIMIT = 200;

function PoliciesPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const currencyParam = Route.useSearch().currency;
  const currency = scopedCurrency(currencyParam);
  // useCurrencyName falls back to Gold's name for a null code, so it is
  // only an answer when a currency is actually scoped. Reading it
  // unguarded is how "All currencies" and the legacy view would both
  // start claiming Gold.
  const scopedName = useCurrencyName(currency ?? null);
  const currencyName = currency ? scopedName : null;
  const [search, setSearch] = useState("");
  const [cancelTarget, setCancelTarget] = useState<FeeProfile | null>(null);

  // The currency rides into the query and the cache key. Filtering the
  // returned rows instead would make every count on the page a count of
  // something else (AC 5), and caching a currency's page under a key
  // that does not name it would serve Red's policies under Gold's
  // heading on the next render.
  const globalQ = useQuery({
    queryKey: feeKeys.profiles("GLOBAL", currencyParam),
    queryFn: () => fetchFeeProfiles("GLOBAL", currencyParam),
    staleTime: 30_000,
  });
  const categoryProfilesQ = useQuery({
    queryKey: feeKeys.profiles("CATEGORY", currencyParam),
    queryFn: () => fetchFeeProfiles("CATEGORY", currencyParam),
    staleTime: 30_000,
  });
  const marketProfilesQ = useQuery({
    queryKey: feeKeys.profiles("MARKET", currencyParam),
    queryFn: () => fetchFeeProfiles("MARKET", currencyParam),
    staleTime: 30_000,
  });
  const scheduledQ = useQuery({
    queryKey: [...feeKeys.all, "scheduled"] as const,
    queryFn: fetchScheduledFeeProfiles,
    staleTime: 30_000,
  });
  const categoriesQ = useQuery({
    queryKey: keys.categoriesList(PAGE_FETCH_LIMIT, 0),
    queryFn: () => fetchCategories({ limit: PAGE_FETCH_LIMIT, offset: 0 }),
  });
  const marketsQ = useQuery({
    queryKey: keys.rulesList(
      "OPEN",
      PAGE_FETCH_LIMIT,
      0,
      { by: "created_at", dir: "desc" },
      {},
    ),
    queryFn: () =>
      fetchRules({
        limit: PAGE_FETCH_LIMIT,
        offset: 0,
        status: "OPEN",
        sort: { by: "created_at", dir: "desc" },
      }),
  });

  const cancelMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      cancelScheduledFeeProfile(id, reason || undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: feeKeys.all });
      setCancelTarget(null);
    },
    meta: { successMessage: "Scheduled fee change canceled." },
  });

  // One platform default PER CURRENCY now, plus possibly the legacy
  // row. Under a currency filter there is at most one; unfiltered there
  // may be several, and the page lists them rather than picking one.
  const globalProfiles = globalQ.data ?? [];
  const categoryProfiles = categoryProfilesQ.data ?? [];
  const marketProfiles = marketProfilesQ.data ?? [];
  const pendingProfiles = scheduledQ.data ?? [];
  const categories = categoriesQ.data?.items ?? [];
  const markets = marketsQ.data?.items ?? [];
  const totalMarketCount = marketsQ.data?.page.total ?? 0;

  // Inheritance counts are computed over the fetched market window.
  // When the catalog outgrows it, the numbers would silently undercount,
  // so the copy drops the figures instead of guessing.
  const windowComplete = markets.length >= totalMarketCount;

  const catMap = useMemo(
    () => new Map(categories.map((c) => [c.category_id, c])),
    [categories],
  );
  const marketNameBase = useMemo(
    () => new Map(markets.map((m) => [m.market_id, m.name])),
    [markets],
  );
  // Market names for override rows, including markets outside the
  // first catalog page (per-id fallback fetch inside the hook).
  const marketScopeIds = useMemo(
    () => [
      ...marketProfiles.map((p) => p.scope_id),
      ...pendingProfiles.filter((p) => p.scope === "MARKET").map((p) => p.scope_id),
    ],
    [marketProfiles, pendingProfiles],
  );
  const marketNames = useMarketNames(marketScopeIds, marketNameBase);

  const marketCategoryName = (marketId: string): string | undefined => {
    const m = markets.find((x) => x.market_id === marketId);
    const cat = m ? catMap.get(m.category_id) : undefined;
    return cat?.name;
  };

  // What each row's markets actually pay on a redemption, and whether
  // the row is what sets it (SM-332).
  //
  // The redemption fee resolves on its own chain, so a row can carry a
  // rate it does not own: a market override that only moves the taker
  // fee still shows the platform's redemption rate. Walking the chain
  // per row is what lets the row say "inherited" instead of implying
  // this is the level to change.
  // Inheritance is walked WITHIN the row's own currency (SM-510): a Red
  // market override inherits from the Red category policy and the Red
  // platform default, never from Gold's. A level that has no policy in
  // that currency falls back to its legacy row, which is exactly what
  // the server does.
  const levelPolicy = (
    rows: FeeProfile[],
    scopeId: string,
    forCurrency: string,
  ): FeeProfile | undefined => {
    const atLevel = rows.filter((r) => r.scope_id === scopeId);
    return (
      atLevel.find((r) => r.currency === forCurrency) ??
      atLevel.find((r) => !r.currency)
    );
  };
  const redeemFor = (p: FeeProfile): { rate: string; inherited: boolean } => {
    if (setsRedemptionRate(p)) {
      return { rate: redemptionRateOf(p), inherited: false };
    }
    if (p.scope === "MARKET") {
      const mkt = markets.find((m) => m.market_id === p.scope_id);
      const cat = mkt
        ? levelPolicy(categoryProfiles, mkt.category_id, p.currency)
        : undefined;
      if (setsRedemptionRate(cat)) {
        return { rate: redemptionRateOf(cat), inherited: true };
      }
    }
    const platform = levelPolicy(globalProfiles, "_global", p.currency);
    return { rate: redemptionRateOf(platform), inherited: true };
  };

  // Inheritance-aware "applies to N markets" for the platform default:
  // markets with their own override or an overridden category don't
  // inherit from it.
  const overriddenMarketIds = useMemo(
    () => new Set(marketProfiles.map((p) => p.scope_id)),
    [marketProfiles],
  );
  const categoriesWithOverride = useMemo(
    () => new Set(categoryProfiles.map((p) => p.scope_id)),
    [categoryProfiles],
  );
  const globalAffectedCount = useMemo(() => {
    let n = 0;
    for (const m of markets) {
      if (overriddenMarketIds.has(m.market_id)) continue;
      if (categoriesWithOverride.has(m.category_id)) continue;
      n++;
    }
    return n;
  }, [markets, overriddenMarketIds, categoriesWithOverride]);
  const marketsByCategory = useMemo(() => {
    const m = new Map<string, number>();
    for (const mkt of markets) {
      m.set(mkt.category_id, (m.get(mkt.category_id) ?? 0) + 1);
    }
    return m;
  }, [markets]);

  // Client-side filter across names (ids also match, for support
  // escalations pasting an id from a ticket).
  const q = search.trim().toLowerCase();
  const matches = (name: string, ...ids: string[]) =>
    !q ||
    name.toLowerCase().includes(q) ||
    ids.some((id) => id.toLowerCase().includes(q));

  // Only the NAME filter runs here. The currency filter ran upstream,
  // so these lists are already the selected currency's — which is what
  // makes the counts beside each heading true.
  const visibleCategories = categoryProfiles.filter((p) =>
    matches(catMap.get(p.scope_id)?.name ?? "", p.scope_id),
  );
  const visibleMarketOverrides = marketProfiles.filter((p) =>
    matches(marketNames.get(p.scope_id) ?? "", p.scope_id),
  );
  // Scheduled changes come back for every currency (there is no filter
  // on that endpoint), so this one narrows locally and says so.
  const currencyMatchesPending = (p: FeeProfile) =>
    currencyParam === undefined ||
    (currencyParam === LEGACY_FEE_CURRENCY ? !p.currency : p.currency === currencyParam);
  const visiblePending = pendingProfiles.filter(currencyMatchesPending).filter((p) => {
    const label =
      p.scope === "GLOBAL"
        ? "Platform default"
        : p.scope === "CATEGORY"
        ? catMap.get(p.scope_id)?.name ?? p.scope_id
        : marketNames.get(p.scope_id) ?? p.scope_id;
    return matches(label, p.scope_id, p.profile_id);
  });

  const loading =
    globalQ.isLoading ||
    categoryProfilesQ.isLoading ||
    marketProfilesQ.isLoading ||
    categoriesQ.isLoading ||
    marketsQ.isLoading;

  // A failed policy fetch must not read as "no policies". Lookup-data
  // failures (categories/markets) only degrade labels, so they don't
  // block the page.
  const failed =
    globalQ.error ?? categoryProfilesQ.error ?? marketProfilesQ.error ?? null;
  const retryFailed = () => {
    if (globalQ.isError) void globalQ.refetch();
    if (categoryProfilesQ.isError) void categoryProfilesQ.refetch();
    if (marketProfilesQ.isError) void marketProfilesQ.refetch();
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Fee policies</h1>
        <p className="max-w-[75ch] text-sm text-muted">
          Taker, maker and redeem fees for every market. Each one charges the
          most specific rate set for it: its own override first, then its
          category&apos;s, then the platform default. The three resolve
          separately, so a market can take its trading fees from an override
          while still inheriting the redemption fee.
        </p>
      </div>

      <p className="max-w-[75ch] rounded-md border border-line-1 bg-surface-2 px-3 py-2.5 text-2xs leading-relaxed text-t-2">
        {currency
          ? `Listed below: the policies that price ${currencyName}. Each policy sets the rate for one currency, so saving one here leaves the others exactly as they are.`
          : currencyParam === LEGACY_FEE_CURRENCY
          ? "Listed below: the policies set before fees were split by currency. Each one still prices every book its markets run. Saving a policy for a currency replaces it for that currency and leaves the others on this rate."
          : "Listed below: every policy, each one naming the currency it prices. Rows marked for every currency were set before fees were split, and still price every book their markets run."}
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <Input
          className="min-w-56 flex-1"
          placeholder="Filter by market or category name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <FeeCurrencyFilter
          value={currencyParam}
          onChange={(next) =>
            navigate({
              to: "/treasury/fees/policies",
              search: feeCurrencySearch(next),
              replace: true,
            })
          }
        />
      </div>

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : failed ? (
        <LoadFailed what="fee policies" error={failed} onRetry={retryFailed} />
      ) : (
        <div className="space-y-6">
          <PolicySection
            heading="Platform default"
            count={globalProfiles.length}
            caption="The rate every market falls back to. One per currency."
            action={<NewOverrideLink scope="platform" />}
          >
            {globalProfiles.length === 0 ? (
              <PolicyEmpty
                text={
                  currencyName
                    ? `No platform default for ${currencyName} yet. Markets fall back to whatever their level was charging before fees were split by currency, and to nothing when no such rate exists.`
                    : "No platform default found. The migration normally seeds one; check with engineering."
                }
              />
            ) : (
              globalProfiles.map((p) => {
                const redeem = redeemFor(p);
                return (
                  <PolicyRow
                    key={p.profile_id}
                    profileId={p.profile_id}
                    name="Platform default"
                    sub={`Version ${p.version.replace(/^v/, "")}`}
                    taker={p.taker_fee_rate}
                    maker={p.maker_fee_rate}
                    redeem={redeem.rate}
                    redeemInherited={redeem.inherited}
                    precision={p.fee_rate_precision}
                    currency={p.currency}
                    affected={
                      windowComplete
                        ? `applies to ${globalAffectedCount} of ${totalMarketCount} markets`
                        : `applies to markets without a more specific override`
                    }
                    updated={p.effective_at}
                  />
                );
              })
            )}
          </PolicySection>

          <PolicySection
            heading="Category overrides"
            count={categoryProfiles.length}
            caption="One rate for every market in the category, unless a market has its own override."
            action={
              categories.length > categoryProfiles.length ? (
                <NewOverrideLink scope="category" />
              ) : null
            }
          >
            {visibleCategories.length === 0 ? (
              <PolicyEmpty
                text={
                  q
                    ? "No category overrides match the filter."
                    : currencyName
                    ? `No category overrides for ${currencyName} yet. Every category takes the platform rate for it.`
                    : "No category overrides yet. Every category uses the platform default."
                }
              />
            ) : (
              visibleCategories.map((p) => {
                const total = marketsByCategory.get(p.scope_id) ?? 0;
                const owned = markets.filter(
                  (m) =>
                    m.category_id === p.scope_id &&
                    overriddenMarketIds.has(m.market_id),
                ).length;
                const redeem = redeemFor(p);
                return (
                  <PolicyRow
                    key={p.profile_id}
                    profileId={p.profile_id}
                    name={catMap.get(p.scope_id)?.name ?? p.scope_id}
                    taker={p.taker_fee_rate}
                    maker={p.maker_fee_rate}
                    redeem={redeem.rate}
                    redeemInherited={redeem.inherited}
                    precision={p.fee_rate_precision}
                    currency={p.currency}
                    affected={
                      !windowComplete
                        ? "applies to its markets without their own override"
                        : owned > 0
                        ? `applies to ${total - owned} of ${total} markets`
                        : `applies to ${total} markets`
                    }
                    updated={p.effective_at}
                  />
                );
              })
            )}
          </PolicySection>

          <PolicySection
            heading="Market overrides"
            count={marketProfiles.length}
            caption="The most specific rate. Applies to exactly one market."
            action={<NewOverrideLink scope="market" />}
          >
            {visibleMarketOverrides.length === 0 ? (
              <PolicyEmpty
                text={
                  q
                    ? "No market overrides match the filter."
                    : currencyName
                    ? `No market overrides for ${currencyName} yet.`
                    : "No market overrides yet."
                }
              />
            ) : (
              visibleMarketOverrides.map((p) => {
                const redeem = redeemFor(p);
                return (
                  <PolicyRow
                    key={p.profile_id}
                    profileId={p.profile_id}
                    name={marketNames.get(p.scope_id) ?? p.scope_id}
                    sub={marketCategoryName(p.scope_id)}
                    taker={p.taker_fee_rate}
                    maker={p.maker_fee_rate}
                    redeem={redeem.rate}
                    redeemInherited={redeem.inherited}
                    precision={p.fee_rate_precision}
                    currency={p.currency}
                    updated={p.effective_at}
                  />
                );
              })
            )}
          </PolicySection>

          {pendingProfiles.length > 0 && (
            <PolicySection
              heading="Scheduled changes"
              count={pendingProfiles.length}
              caption="Saved ahead of time. The current rate stays in effect until each change activates."
            >
              {visiblePending.length === 0 ? (
                <PolicyEmpty
                  text={
                    currencyName
                      ? `No scheduled change prices ${currencyName}.`
                      : "No scheduled changes match the filter."
                  }
                />
              ) : (
              visiblePending.map((p) => (
                <PolicyRow
                  key={p.profile_id}
                  profileId={p.profile_id}
                  name={
                    p.scope === "GLOBAL"
                      ? "Platform default"
                      : p.scope === "CATEGORY"
                      ? catMap.get(p.scope_id)?.name ?? p.scope_id
                      : marketNames.get(p.scope_id) ?? p.scope_id
                  }
                  sub={scopeNoun(p.scope)}
                  taker={p.taker_fee_rate}
                  maker={p.maker_fee_rate}
                  redeem={redemptionRateOf(p)}
                  redeemInherited={!setsRedemptionRate(p)}
                  precision={p.fee_rate_precision}
                  currency={p.currency}
                  affected={`activates ${fmtRelative(p.effective_at)}`}
                  updated={p.created_at}
                  trailingAction={
                    <button
                      type="button"
                      aria-label="Cancel scheduled change"
                      title="Cancel this scheduled change"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCancelTarget(p);
                      }}
                      className="rounded-md p-1 text-muted hover:bg-bg-2 hover:text-danger"
                    >
                      <CloseIcon className="h-3.5 w-3.5" />
                    </button>
                  }
                />
              ))
              )}
            </PolicySection>
          )}
        </div>
      )}

      <ConfirmModal
        open={cancelTarget !== null}
        tone="danger"
        title="Cancel this scheduled change?"
        description={
          <p>
            It won&apos;t activate. The rate that is live right now stays in
            effect.
          </p>
        }
        confirmLabel="Cancel change"
        cancelLabel="Keep it"
        reasonLabel="Reason (audit log)"
        reasonRequired
        loading={cancelMut.isPending}
        error={cancelMut.isError ? (cancelMut.error as Error).message : null}
        onConfirm={(reason) =>
          cancelTarget &&
          cancelMut.mutate({ id: cancelTarget.profile_id, reason })
        }
        onCancel={() => {
          cancelMut.reset();
          setCancelTarget(null);
        }}
      />
    </div>
  );
}

function NewOverrideLink({
  scope,
}: {
  scope: "market" | "category" | "platform";
}) {
  return (
    <Link
      to="/treasury/fees/policies/new"
      search={{ scope }}
      className="rounded-md border border-line-1 bg-bg-2 px-2.5 py-1 text-2xs font-medium text-t-2 hover:border-line-3 hover:text-fg"
    >
      {scope === "platform" ? "+ New default" : "+ New override"}
    </Link>
  );
}

function CloseIcon(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
